import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { Plus, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_BASE_URL } from '../config/config';
import OpsLayout from '../components/OpsLayout';
import '../css/HospitalManagement.css';

// 기관(병원) 마스터 관리.
//
// sdss_cd.hospital_info 는 전국 기관 78,000여 건이 담긴 마스터다. 그런데 복지관·
// 센터처럼 그 자료에 없는 기관이 생길 때마다 사람이 직접 INSERT 해 왔다
// (encrypted_code 가 'MANUAL-…' 인 행). 그 작업을 여기서 한다.
//
// ⚠️ 지역·구군·기관종류는 코드와 이름이 짝으로 비정규화돼 있다. 그래서 직접
//    입력받지 않고 기존 값에서 고르게 한다 — 한쪽만 새로 넣으면 지역 목록
//    (/api/regions 는 hospital_info 를 DISTINCT 한다)이 갈라진다.
//
// ⚠️ 삭제 기능은 두지 않는다. app_user_group_info.hospital_id 가 이 표를 참조하는데
//    FK 가 없어서, 지우면 그룹이 없는 기관을 가리킨 채 남는다.

const PAGE_SIZE = 20;

const EMPTY_FORM = {
    institution_name: '',
    type_code: '',
    type_name: '',
    region_code: '',
    region_name: '',
    district_code: '',
    district_name: '',
    subdistrict: '',
    postal_code: '',
    address: ''
};

const readRole = () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) return null;
        const decoded = jwtDecode(token);
        return decoded?.role || decoded?.user_role || null;
    } catch (error) {
        return null;
    }
};

// 서버가 주는 문구를 그대로 보여준다. 없으면 상태 코드로 다음 행동을 안내한다.
const describeError = (error, fallback) => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;
    if (message) return message;
    if (status === 401) return '세션이 만료되었습니다. 다시 로그인해 주세요.';
    if (status === 403) return '권한이 없습니다. 최고관리자 계정으로 로그인해 주세요.';
    if (error && !error.response) return '서버에 연결하지 못했습니다. 네트워크를 확인해 주세요.';
    return fallback;
};

const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const TITLE = '기관 관리';
const DESCRIPTION = '전국 기관 마스터. 자료에 없는 기관을 여기서 등록합니다.';

export default function HospitalManagement() {
    const [role] = useState(readRole);
    const isSuperAdmin = role === 'SUPER_ADMIN';

    const [hospitals, setHospitals] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [listError, setListError] = useState(null);

    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [regionFilter, setRegionFilter] = useState('');
    const [districtFilter, setDistrictFilter] = useState('');

    const [regions, setRegions] = useState([]);
    const [filterDistricts, setFilterDistricts] = useState([]);
    const [types, setTypes] = useState([]);

    // 등록/수정 폼. editingId 가 null 이면 신규 등록이다.
    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formDistricts, setFormDistricts] = useState([]);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);
    const [notice, setNotice] = useState(null);

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    const loadHospitals = useCallback(async () => {
        setLoading(true);
        setListError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/hospitals`, {
                ...authHeaders(),
                params: {
                    page, limit: PAGE_SIZE,
                    search: search || undefined,
                    region_name: regionFilter || undefined,
                    district_name: districtFilter || undefined
                }
            });
            setHospitals(response.data?.hospitals || []);
            setTotalCount(response.data?.totalCount || 0);
        } catch (error) {
            console.error('기관 목록 조회 실패:', error);
            setHospitals([]);
            setListError(describeError(error, '기관 목록을 불러오지 못했습니다.'));
        } finally {
            setLoading(false);
        }
    }, [page, search, regionFilter, districtFilter]);

    useEffect(() => { loadHospitals(); }, [loadHospitals]);

    // 지역·기관종류 선택지. 화면을 여는 동안 한 번만 받는다.
    useEffect(() => {
        (async () => {
            try {
                const [regionRes, typeRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/regions`, authHeaders()),
                    axios.get(`${API_BASE_URL}/api/hospital-types`, authHeaders())
                ]);
                setRegions(regionRes.data?.regions || []);
                setTypes(typeRes.data?.types || []);
            } catch (error) {
                console.error('선택지 조회 실패:', error);
            }
        })();
    }, []);

    const fetchDistricts = useCallback(async (regionName) => {
        if (!regionName) return [];
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/districts/${encodeURIComponent(regionName)}`, authHeaders()
            );
            return response.data?.districts || [];
        } catch (error) {
            console.error('구/군 조회 실패:', error);
            return [];
        }
    }, []);

    useEffect(() => {
        (async () => setFilterDistricts(await fetchDistricts(regionFilter)))();
        setDistrictFilter('');
    }, [regionFilter, fetchDistricts]);

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setFormDistricts([]);
        setFormError(null);
        setFormOpen(true);
    };

    const openEdit = async (hospital) => {
        setEditingId(hospital.id);
        setForm({
            institution_name: hospital.institution_name || '',
            type_code: hospital.type_code ?? '',
            type_name: hospital.type_name || '',
            region_code: hospital.region_code ?? '',
            region_name: hospital.region_name || '',
            district_code: hospital.district_code ?? '',
            district_name: hospital.district_name || '',
            subdistrict: hospital.subdistrict || '',
            postal_code: hospital.postal_code || '',
            address: hospital.address || ''
        });
        setFormDistricts(await fetchDistricts(hospital.region_name));
        setFormError(null);
        setFormOpen(true);
    };

    // 지역을 고르면 코드와 이름을 함께 담는다. 둘이 어긋나면 목록이 갈라진다.
    const onRegionPick = async (regionName) => {
        const picked = regions.find((r) => r.region_name === regionName);
        setForm((prev) => ({
            ...prev,
            region_name: regionName,
            region_code: picked ? picked.region_code : '',
            district_name: '',
            district_code: ''
        }));
        setFormDistricts(await fetchDistricts(regionName));
    };

    const onDistrictPick = (districtName) => {
        const picked = formDistricts.find((d) => d.district_name === districtName);
        setForm((prev) => ({
            ...prev,
            district_name: districtName,
            district_code: picked ? picked.district_code : ''
        }));
    };

    const onTypePick = (value) => {
        // value 는 "code|name". 짝을 깨지 않으려고 한 값으로 묶어 고른다.
        const [code, name] = value.split('|');
        setForm((prev) => ({ ...prev, type_code: code || '', type_name: name || '' }));
    };

    const save = async (event) => {
        event.preventDefault();
        setSaving(true);
        setFormError(null);
        try {
            if (editingId) {
                await axios.put(`${API_BASE_URL}/api/hospitals/${editingId}`, form, authHeaders());
                setNotice(`'${form.institution_name}' 을(를) 수정했습니다.`);
            } else {
                await axios.post(`${API_BASE_URL}/api/hospitals`, form, authHeaders());
                setNotice(`'${form.institution_name}' 을(를) 등록했습니다.`);
            }
            setFormOpen(false);
            setPage(1);
            await loadHospitals();
        } catch (error) {
            console.error('기관 저장 실패:', error);
            setFormError(describeError(error, '저장하지 못했습니다.'));
        } finally {
            setSaving(false);
        }
    };

    const typeValue = useMemo(
        () => (form.type_code === '' ? '' : `${form.type_code}|${form.type_name}`),
        [form.type_code, form.type_name]
    );

    if (!isSuperAdmin) {
        return (
            <OpsLayout sideNav={false} title={TITLE} description={DESCRIPTION}>
                <div className="sds-notice sds-notice--error">
                    이 화면은 최고관리자만 사용할 수 있습니다.
                </div>
            </OpsLayout>
        );
    }

    return (
        <OpsLayout
            sideNav={false}
            title={TITLE}
            description={DESCRIPTION}
            actions={
                <button className="sds-btn sds-btn--primary" onClick={openCreate}>
                    <Plus size={16} aria-hidden="true" />기관 등록
                </button>
            }
        >
            <section className="ops-page__card hospital-page">
                {notice && (
                    <div className="sds-notice">
                        {notice}
                        <span className="sds-toolbar__spacer" />
                        <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={() => setNotice(null)} aria-label="닫기">
                            <X size={16} aria-hidden="true" />
                        </button>
                    </div>
                )}

                <div className="sds-toolbar">
                    <label className="hospital-page__filter">
                        지역
                        <select
                            className="sds-select"
                            value={regionFilter}
                            onChange={(e) => { setPage(1); setRegionFilter(e.target.value); }}
                        >
                            <option value="">전체</option>
                            {regions.map((r) => (
                                <option key={r.region_code} value={r.region_name}>{r.region_name}</option>
                            ))}
                        </select>
                    </label>
                    <label className="hospital-page__filter">
                        구/군
                        <select
                            className="sds-select"
                            value={districtFilter}
                            disabled={!regionFilter}
                            onChange={(e) => { setPage(1); setDistrictFilter(e.target.value); }}
                        >
                            <option value="">전체</option>
                            {filterDistricts.map((d) => (
                                <option key={d.district_code} value={d.district_name}>{d.district_name}</option>
                            ))}
                        </select>
                    </label>
                    <label className="hospital-page__filter">
                        기관명
                        <input
                            className="sds-input"
                            value={searchInput}
                            placeholder="기관명 일부"
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); setSearch(searchInput); } }}
                        />
                    </label>
                    <button className="sds-btn sds-btn--secondary" onClick={() => { setPage(1); setSearch(searchInput); }}>
                        검색
                    </button>
                    <span className="sds-toolbar__spacer" />
                    <span className="ops-page__count"><b>{totalCount.toLocaleString()}</b>건</span>
                </div>

                {listError && <div className="sds-notice sds-notice--error">{listError}</div>}

                {loading ? (
                    <div className="sds-notice">불러오는 중…</div>
                ) : (
                    <div className="sds-table-wrap">
                        <table className="sds-table">
                            <thead>
                                <tr>
                                    <th>ID</th><th>기관명</th><th>종류</th><th>지역</th>
                                    <th>구/군</th><th>읍/면/동</th><th>우편번호</th><th>주소</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {hospitals.map((h) => (
                                    <tr key={h.id}>
                                        <td>{h.id}</td>
                                        <td>{h.institution_name}</td>
                                        <td>{h.type_name}</td>
                                        <td>{h.region_name}</td>
                                        <td>{h.district_name}</td>
                                        <td>{h.subdistrict}</td>
                                        <td>{h.postal_code}</td>
                                        <td>{h.address}</td>
                                        <td>
                                            <button className="sds-btn sds-btn--secondary sds-btn--sm" onClick={() => openEdit(h)}>수정</button>
                                        </td>
                                    </tr>
                                ))}
                                {hospitals.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className="sds-empty">조건에 맞는 기관이 없습니다.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="sds-pagination">
                    <span className="sds-pagination__info">{page} / {totalPages}</span>
                    <button
                        className="sds-pagination__btn"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                        aria-label="이전 페이지"
                    >
                        <ChevronLeft size={16} aria-hidden="true" />
                    </button>
                    <button
                        className="sds-pagination__btn"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                        aria-label="다음 페이지"
                    >
                        <ChevronRight size={16} aria-hidden="true" />
                    </button>
                </div>
            </section>

            {formOpen && (
                <div className="sds-modal-overlay" onClick={() => setFormOpen(false)}>
                    <div
                        className="sds-modal sds-modal--wide"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="hospital-modal-title"
                    >
                        {/* 폼 안의 버튼은 type 없이 만들어졌다 — 만들 때는 전역 CSS 때문에 type 을 쓸 수 없었다.
                            그래서 저장/수정 버튼만 기본 submit 으로 두고, 그 외(취소·닫기)는 onClick 에서
                            e.preventDefault() 로 제출을 막는다. 지금 규칙(design.md 6.7)은 폼 안의 보조
                            버튼에 type="button" 을 쓰는 것이다 — 이 폼을 고칠 때 그쪽으로 맞춘다. */}
                        <form onSubmit={save}>
                            <div className="sds-modal__head">
                                <h2 id="hospital-modal-title" className="sds-modal__title">
                                    {editingId ? `기관 수정 (ID ${editingId})` : '기관 등록'}
                                </h2>
                                <button
                                    className="sds-btn sds-btn--ghost sds-btn--sm"
                                    onClick={(e) => { e.preventDefault(); setFormOpen(false); }}
                                    aria-label="닫기"
                                >
                                    <X size={16} aria-hidden="true" />
                                </button>
                            </div>

                            <div className="sds-modal__body">
                                {formError && <div className="sds-notice sds-notice--error">{formError}</div>}

                                <div className="hospital-form-grid">
                                    <div className="sds-field hospital-field-wide">
                                        <label className="sds-label">기관명<span className="sds-required">*</span></label>
                                        <input
                                            className="sds-input"
                                            maxLength={255} required
                                            value={form.institution_name}
                                            onChange={(e) => setForm({ ...form, institution_name: e.target.value })}
                                        />
                                    </div>

                                    <div className="sds-field">
                                        <label className="sds-label">기관 종류<span className="sds-required">*</span></label>
                                        <select className="sds-select" value={typeValue} required onChange={(e) => onTypePick(e.target.value)}>
                                            <option value="">선택</option>
                                            {types.map((t) => (
                                                <option key={`${t.type_code}|${t.type_name}`} value={`${t.type_code}|${t.type_name}`}>
                                                    {t.type_name} ({t.type_code})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="sds-field">
                                        <label className="sds-label">지역<span className="sds-required">*</span></label>
                                        <select className="sds-select" value={form.region_name} required onChange={(e) => onRegionPick(e.target.value)}>
                                            <option value="">선택</option>
                                            {regions.map((r) => (
                                                <option key={r.region_code} value={r.region_name}>{r.region_name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="sds-field">
                                        <label className="sds-label">구/군<span className="sds-required">*</span></label>
                                        <select
                                            className="sds-select"
                                            value={form.district_name} required disabled={!form.region_name}
                                            onChange={(e) => onDistrictPick(e.target.value)}
                                        >
                                            <option value="">선택</option>
                                            {formDistricts.map((d) => (
                                                <option key={d.district_code} value={d.district_name}>{d.district_name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="sds-field">
                                        <label className="sds-label">읍/면/동<span className="sds-required">*</span></label>
                                        <input
                                            className="sds-input"
                                            maxLength={100} required
                                            value={form.subdistrict}
                                            onChange={(e) => setForm({ ...form, subdistrict: e.target.value })}
                                        />
                                    </div>

                                    <div className="sds-field">
                                        <label className="sds-label">우편번호<span className="sds-required">*</span></label>
                                        <input
                                            className="sds-input"
                                            maxLength={100} required
                                            value={form.postal_code}
                                            onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                                        />
                                    </div>

                                    <div className="sds-field hospital-field-wide">
                                        <label className="sds-label">주소<span className="sds-required">*</span></label>
                                        <input
                                            className="sds-input"
                                            maxLength={255} required
                                            value={form.address}
                                            onChange={(e) => setForm({ ...form, address: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="sds-notice sds-notice--info">
                                    지역·구군·종류는 기존 값에서 고릅니다 — 코드와 이름이 짝으로 저장돼 있어
                                    새 값을 직접 넣으면 다른 화면의 목록이 갈라집니다.
                                    {!editingId && ' 등록 시 기관코드는 MANUAL- 로 시작하도록 자동 생성됩니다.'}
                                </div>
                            </div>

                            <div className="sds-modal__foot">
                                <button
                                    className="sds-btn sds-btn--secondary"
                                    onClick={(e) => { e.preventDefault(); setFormOpen(false); }}
                                >
                                    취소
                                </button>
                                <button className="sds-btn sds-btn--primary" disabled={saving}>
                                    {saving ? '저장 중…' : (editingId ? '수정' : '등록')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </OpsLayout>
    );
}
